import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const guideCardVariants = cva(
  'c-study-guide-card ' +
    'relative flex cursor-pointer flex-wrap items-start gap-3 rounded-[20px] border-2 border-solid border-neutral-lightest bg-white p-4 transition-all duration-150 ' +
    'hover:shadow-[0px_4px_10px_3px_rgba(93,106,134,0.2)] ' +
    'focus:shadow-[0px_4px_10px_3px_rgba(93,106,134,0.2)]',
  {
    variants: {
      isCurrent: {
        true: [
          // Estilos base
          'border border-solid border-[#F99153] bg-[linear-gradient(9.59deg,#FFF_20.02%,#F5F5F5_85.13%)] shadow-[0px_4px_10px_0px_rgba(93,106,134,0.05)] p-8 px-4 [&_.c-study-guide-card-title]:text-2xl',
          // Estilos para GuideCardCheck
          '[&_.c-study-guide-card-check]:mt-1'
        ]
      }
    },
    defaultVariants: {
      isCurrent: false
    }
  }
);

const GuideCard = React.forwardRef(({ isCurrent, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(guideCardVariants({ isCurrent }), className)}
    tabIndex={0}
    {...props}
  />
));
GuideCard.displayName = 'GuideCard';

const GuideCardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex max-w-full flex-grow flex-col gap-2', className)}
    {...props}
  />
));
GuideCardContent.displayName = 'CardContent';

const GuideCardHat = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('w-full text-xs font-light text-neutral-darker', 'mb-0', className)}
    {...props}
  />
));
GuideCardHat.displayName = 'GuideCardHat';

const GuideCardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('c-study-guide-card-title', 'mb-0 text-base font-bold leading-none text-primary-dark', className)}
    {...props}
  />
));
GuideCardTitle.displayName = 'GuideCardTitle';

const GuideCardTime = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-shrink-0 items-center gap-1 self-center leading-none text-neutral-light', className)}
    {...props}
  />
));
GuideCardTime.displayName = 'GuideCardTime';

const GuideCardIcon = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('[&>svg]:w-5', className)}
    {...props}
  />
));
GuideCardIcon.displayName = 'GuideCardIcon';

const GuideCardCheck = React.forwardRef(({ className, ...props }, ref) => {
  const [isChecked, setIsChecked] = React.useState(props.checked || false);

  const handleToggle = event => {
    setIsChecked(event.target.checked);
  };

  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      <input
        ref={ref}
        type="checkbox"
        className="sr-only"
        checked={isChecked}
        onChange={handleToggle}
        {...props}
      />

      <span
        className={cn(
          'c-study-guide-card-check',
          // Estilos base do checkbox visual
          'inline-flex items-center justify-center',
          'm-0 h-5 w-5 shrink-0 appearance-none',
          'rounded-full border border-solid border-neutral-light bg-white',
          'transition-all duration-200 ease-in-out',
          'cursor-pointer bg-center outline-none',
          // Estilos de estado
          isChecked && 'border-success bg-success'
        )}
      >
        {isChecked && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-3 w-3"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        )}
      </span>
    </div>
  );
});
GuideCardCheck.displayName = 'GuideCardCheck';

export { GuideCard, GuideCardContent, GuideCardHat, GuideCardTitle, GuideCardTime, GuideCardIcon, GuideCardCheck };
